import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { getStripe } from '../services/stripe';
import { updateStripeSubscription } from '../services/stripe';
import { DisplayPackage } from '../types/revenuecat';
import { parseDuration } from '../utils/duration';
import { getCurrencySymbol } from '../utils/currency';
import StripePaymentForm from './StripePaymentForm';
import './Billing.css';
import { addDurationToDate } from '../utils/addDurationToDate';
import { getCustomerInfo, hasActiveSubscription } from '../services/revenuecat';
import { clearSubscriptionCache } from '../utils/subscriptionCache';
import { t } from '../i18n';
import { EARLY_BIRD_MIN_FREE_LEAD_MS } from '../utils/earlyBird';
import { formatLongDate } from '../utils/formatDate';

const logo = `${process.env.PUBLIC_URL}/logo.svg`;

const Billing: React.FC = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [stripePromise] = useState(() => getStripe());
  const [selectedPackage, setSelectedPackage] = useState<DisplayPackage | null>(null);
  const [couponCode, setCouponCode] = useState<string | undefined>(undefined);
  const [discountInfo, setDiscountInfo] = useState<any>(null);
  const [isPromotionalUpgrade, setIsPromotionalUpgrade] = useState(false);
  const [promotionalExpiryDate, setPromotionalExpiryDate] = useState<string | null>(null);
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);

  useEffect(() => {
    const initializeBilling = async () => {
      // Get selected package and discount info from navigation state
      const state = location.state as {
        selectedPackage?: DisplayPackage;
        couponCode?: string;
        discountInfo?: any;
        isChangingPlan?: boolean;  // Flag to allow changing plans
        currentSubscriptionExpiry?: string;  // Promotional subscription expiry date
        currentSubscriptionStore?: string;   // Current subscription store type
        currentPriceId?: string;  // Current subscription price ID
        isEarlyBird?: boolean;  // Partner-code runner taking the early bird price
        earlyBirdFreeUntil?: string | null;  // Server's date their code ends; null = charged today
      };

      // Check if user already has an active subscription (unless they're changing plans).
      // Early bird runners are on an active promo by design; the server refuses payers.
      if (currentUser?.uid && !state?.isChangingPlan && !state?.isEarlyBird) {
        try {
          const subscriber = await getCustomerInfo(currentUser.uid);
          if (hasActiveSubscription(subscriber)) {
            // Redirect to subscription details page for all active subscriptions
            navigate('/subscription');
            return;
          }
        } catch (err) {
          console.error('Error checking subscription status:', err);
        }
      }

      if (!state?.selectedPackage) {
        // If no package selected, redirect back to offers
        navigate('/offers');
        return;
      }

      // Check if this is a plan change vs. new subscription
      const isChanging = state?.isChangingPlan === true && !state?.isEarlyBird;
      setIsChangingPlan(isChanging);

      // Check if this is an upgrade from promotional subscription
      // Early bird reuses the promo-upgrade screens while the code is running;
      // once it has ended it's a plain pay-today checkout.
      const isPromo = state?.isEarlyBird
        ? !!state.earlyBirdFreeUntil && new Date(state.earlyBirdFreeUntil).getTime() - Date.now() > EARLY_BIRD_MIN_FREE_LEAD_MS
        : state?.currentSubscriptionStore === 'promotional';
      setIsPromotionalUpgrade(isPromo);
      if (isPromo) {
        setPromotionalExpiryDate((state.isEarlyBird ? state.earlyBirdFreeUntil : state.currentSubscriptionExpiry) || null);
      }

      console.log('Billing component - Promotional upgrade detection:', {
        currentSubscriptionStore: state?.currentSubscriptionStore,
        currentSubscriptionExpiry: state?.currentSubscriptionExpiry,
        isPromo,
        willSet: { isPromo, expiryDate: state.currentSubscriptionExpiry }
      });

      // Set the selected package and discount from navigation state
      setSelectedPackage(state.selectedPackage);
      setCouponCode(state.couponCode);
      setDiscountInfo(state.discountInfo);

      // If changing plan (not promotional), automatically update without payment form
      if (isChanging && !isPromo && currentUser?.uid) {
        handlePlanChange(state.selectedPackage, currentUser.uid);
      }
    };

    initializeBilling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, navigate, currentUser]);

  if (!selectedPackage || isUpdatingPlan) {
    return (
      <div className="billing-container">
        <div className="billing-background" />
        <div className="billing-content">
          <p style={{ color: '#FAFDFB', textAlign: 'center', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            {isUpdatingPlan ? t('billing.updatingPlan') : t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    navigate('/offers');
  };

  const handlePlanChange = async (newPackage: DisplayPackage, userId: string) => {
    setIsUpdatingPlan(true);
    try {
      console.log('Updating plan:', {
        newPriceId: newPackage.product.price_id,
        userId
      });

      const result = await updateStripeSubscription(
        userId,
        newPackage.product.price_id,
        'create_prorations'
      );

      if (result?.success) {
        console.log('Plan updated successfully:', result);
        clearSubscriptionCache(userId);

        // Wait for RevenueCat to sync
        await new Promise(resolve => setTimeout(resolve, 2000));

        alert(t('billing.planUpdatedSuccess'));
        navigate('/subscription?refresh=true');
      } else {
        throw new Error(result?.message || t('billing.failedToUpdatePlan'));
      }
    } catch (err) {
      console.error('Error updating plan:', err);
      alert(t('billing.failedToUpdatePlan'));
      navigate('/offers');
    } finally {
      setIsUpdatingPlan(false);
    }
  };

  // Parse subscription duration from ISO format
  let subscriptionDuration = '';
  if (selectedPackage.product.duration) {
    const parsed = parseDuration(selectedPackage.product.duration);
    subscriptionDuration = parsed === 'Unknown' ? (selectedPackage.product.title || 'Subscription') : parsed;
  } else {
    subscriptionDuration = selectedPackage.product.title || 'Subscription';
  }

  // Calculate current subscription's remaining days (when changing plans)
  let currentSubscriptionDaysRemaining = 0;
  let currentSubscriptionExpiryFormatted = '';
  if (isPromotionalUpgrade && promotionalExpiryDate) {
    const now = new Date();
    const expiryDate = new Date(promotionalExpiryDate);
    currentSubscriptionDaysRemaining = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    currentSubscriptionExpiryFormatted = formatLongDate(expiryDate);
  }

  // Only show trial if valid ISO string and not empty/null
  let trialDuration: string | null = null;
  if (selectedPackage.product.trial_duration) {
    const parsedTrial = parseDuration(selectedPackage.product.trial_duration);
    trialDuration = (parsedTrial !== 'Unknown' && parsedTrial !== selectedPackage.product.trial_duration) ? parsedTrial : null;
  }

  // Calculate price per period
  let price = (selectedPackage.product.price || 0) / 100; // Convert cents to currency
  let originalPrice: number | null = null;
  let discountAmount: number | null = null;

  // Get currency symbol (default to GBP if not specified)
  const currency = selectedPackage.product.currency || 'gbp';
  const currencySymbol = getCurrencySymbol(currency);

  // Apply discount if available
  if (discountInfo?.pricing) {
    originalPrice = price;
    price = discountInfo.pricing.discountedAmount / 100;
    discountAmount = (discountInfo.pricing.originalAmount - discountInfo.pricing.discountedAmount) / 100;
  }

  const priceString = `${currencySymbol}${price.toFixed(2)}`;

  // Calculate monthly price if yearly
  const isYearly = subscriptionDuration.toLowerCase().includes('year');
  const monthlyPrice = isYearly ? (price / 12).toFixed(2) : null;

  return (
    <div className="billing-container">
      <div className="billing-background" />

      {/* Back button */}
      <button className="billing-back-button" onClick={handleBack} aria-label="Go back">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      <div className="billing-content">
        {/* Left Side - Subscription Details */}
        <div className="billing-left">
          <a href="https://urunn.com" style={{ cursor: 'pointer' }}>
            <img src={logo} alt="Urunn" className="billing-logo" />
          </a>

          <div className="subscription-details">
            <p className="subscribe-to">{t('billing.subscribeTo')}</p>
            <h1 className="plan-name">{subscriptionDuration}</h1>

            <div className="price-display">
              {originalPrice && (
                <span className="original-price">{currencySymbol}{originalPrice.toFixed(2)}</span>
              )}
              <h2 className="plan-price">{priceString}</h2>
            </div>

            {discountInfo?.coupon && (
              <div className="discount-applied">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 0 1-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582ZM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 0 1-.567.267Z" fill="#4CAF50"/>
                  <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM9 5a1 1 0 0 0-1 1v.006a3.355 3.355 0 0 0-1.205.508C6.215 6.964 6 7.682 6 8c0 .318.215 1.036.795 1.486.373.29.776.508 1.205.582V12c0 .27.105.52.278.712l.004.004c.173.192.417.322.673.346a2.305 2.305 0 0 0 .567-.267C10.93 12.34 11 12.114 11 12c0-.318-.215-1.036-.795-1.486a3.355 3.355 0 0 1-1.205-.582V8.006c.22-.071.412-.164.567-.267C10.93 7.34 11 7.114 11 7c0-.27-.105-.52-.278-.712l-.004-.004A.996.996 0 0 0 10 6a1 1 0 0 0-1-1Z" clipRule="evenodd" fill="#4CAF50"/>
                </svg>
                <span>{t('billing.discountApplied', { name: discountInfo.coupon.name || t('billing.defaultDiscountName'), amount: `${currencySymbol}${discountAmount?.toFixed(2)}` })}</span>
              </div>
            )}

            {trialDuration && (
              <div className="trial-badge">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2ZM10 3C13.8777 3 17 6.12229 17 10C17 13.8777 13.8777 17 10 17C6.12229 17 3 13.8777 3 10C3 6.12229 6.12229 3 10 3ZM10 5C9.72386 5 9.5 5.22386 9.5 5.5V10.5C9.5 10.7761 9.72386 11 10 11H13.5C13.7761 11 14 10.7761 14 10.5C14 10.2239 13.7761 10 13.5 10H10.5V5.5C10.5 5.22386 10.2761 5 10 5Z" fill="currentColor"/>
                </svg>
                <span>{t('billing.freeTrial', { duration: trialDuration })}</span>
              </div>
            )}

            {monthlyPrice && (
              <p className="monthly-price">{trialDuration ? t('billing.monthlyPriceAfterTrial', { price: `${currencySymbol}${monthlyPrice}` }) : t('billing.monthlyPrice', { price: `${currencySymbol}${monthlyPrice}` })}</p>
            )}
          </div>

          <div className="billing-summary">
            {/* Current Subscription Details (when changing plans) */}
            {(isPromotionalUpgrade && promotionalExpiryDate) && (
              <>
                <div className="summary-section">
                  <h3 className="summary-section-title">{t('billing.currentSubscription')}</h3>
                  <div className="summary-row current-subscription">
                    <span>{t('billing.remaining')}</span>
                    <span>{currentSubscriptionDaysRemaining} day{currentSubscriptionDaysRemaining !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="summary-row current-subscription">
                    <span>{t('billing.until')}</span>
                    <span>{currentSubscriptionExpiryFormatted}</span>
                  </div>
                </div>
              </>
            )}

            {isPromotionalUpgrade && promotionalExpiryDate && (
              <div className="summary-row promotional-upgrade">
                <span>{t('billing.chargedOn')}</span>
                <span>{formatLongDate(promotionalExpiryDate)}</span>
              </div>
            )}
            {trialDuration && selectedPackage.product.trial_duration && !isPromotionalUpgrade && (
              <div className="summary-row">
                <span>{t('billing.afterTrialEnds', { date: addDurationToDate(new Date(), selectedPackage.product.trial_duration) })}</span>
                <span>{currencySymbol}{price.toFixed(2)}</span>
              </div>
            )}
            <div className="summary-row total">
              <span>{trialDuration || isPromotionalUpgrade ? t('billing.totalDueToday') : t('billing.dueToday')}</span>
              <span>{(trialDuration || isPromotionalUpgrade) ? `${currencySymbol}0.00` : `${currencySymbol}${price.toFixed(2)}`}</span>
            </div>
          </div>
        </div>

        {/* Right Side - Payment Form or Plan Change Summary */}
        <div className="billing-right">
          {isChangingPlan && !isPromotionalUpgrade ? (
            <div className="billing-form" style={{ textAlign: 'center', padding: '40px 20px' }}>
              <h2 style={{ color: '#1B2B48', marginBottom: '16px' }}>{t('billing.planUpdate')}</h2>
              <p style={{ color: '#1B2B48', opacity: 0.7, marginBottom: '24px' }}>
                {t('billing.planUpdateMessage')}
              </p>
              <button
                onClick={() => handlePlanChange(selectedPackage, currentUser?.uid || '')}
                disabled={isUpdatingPlan}
                className="btn-start-trial"
              >
                {isUpdatingPlan ? t('billing.updating') : t('billing.confirmChange')}
              </button>
              <p style={{ color: '#1B2B48', fontSize: '14px', opacity: 0.6, marginTop: '16px' }}>
                {t('billing.noPaymentRequired')}
              </p>
            </div>
          ) : (
            <Elements stripe={stripePromise}>
              <StripePaymentForm
                selectedPackage={selectedPackage}
                userEmail={currentUser?.email || ''}
                userId={currentUser?.uid || ''}
                couponCode={couponCode}
                isPromotionalUpgrade={isPromotionalUpgrade}
                promotionalExpiryDate={promotionalExpiryDate}
              />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
};

export default Billing;
